import { useCallback, useMemo, useState } from "react";
import { ViewStateChangeEvent } from "react-map-gl/maplibre";

import { useDebounce, useLocalStorage } from "@nens/react/hooks";
import { convertLngLatBoundsToExtent } from "@nens/react-map-gl/utils";
import { Extent, removeNullUndefined } from "@nens/shared-utils";
import { Schematisation } from "@nens/threedi/openapi-client";

import { REVISION_ID_KEY } from "@/config/query-parameter-consts";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useQuerySearchState } from "@/hooks/use-query-search-state";
import { useAllThreediModelsQuery } from "@/queries/query-hcc-models";
import {
  SchematisationWithId,
  useAllHccSchematisationsListQuery,
  useGetHccSchematisationQuery,
  useTenantHasSchematisationsQuery,
} from "@/queries/query-hcc-schematisation";
import { combineHccModelExtents } from "@/utils/schematisation-extents";

import { SchematisationMapItem } from "../features/map/types/schematisation-map-item";

export const SCHEMATISATION_SEARCH_KEY = "schematisation";

type UseSchematisationsOverviewProps = {
  disableSettingSearchParams?: boolean;
  isSectionExpanded: boolean;
};

const extentIntersects = (a: Extent, b: Extent) => {
  const [aMinLng, aMinLat, aMaxLng, aMaxLat] = a;
  const [bMinLng, bMinLat, bMaxLng, bMaxLat] = b;

  // Check if two bounding boxes overlap using AABB collision detection.
  // Extents intersect unless one box is completely outside the other on any axis.
  return !(aMaxLng < bMinLng || aMinLng > bMaxLng || aMaxLat < bMinLat || aMinLat > bMaxLat);
};

export function useSchematisationsOverview({
  disableSettingSearchParams,
  isSectionExpanded,
}: UseSchematisationsOverviewProps) {
  const [_showingMap, setShowingMap] = useLocalStorage<boolean>(
    "showingMapForSchematisations",
    false
  );
  const showingMap = isSectionExpanded && _showingMap;

  const { searchString, setSearchString } = useRanaTableData({
    initialOrderings: { lastActivity: "za" },
    initialPage: 1,
    keyAddon: "schematisation",
    initialPageSize: 10,
    hasSearchParams: !disableSettingSearchParams,
  });
  const [selectedTag, setSelectedTag] = useRanaSearchState<string | null>(
    "tag",
    null,
    ["string", "null"],
    { disableSettingParams: disableSettingSearchParams }
  );

  const [filterMap, setFilterMap] = useLocalStorage("filterMapSchematisation", false);
  const [mapExtent, setMapExtent] = useState<Extent | null>(null);

  const shouldFilterByMap = filterMap && !!mapExtent;

  const onMapMoveEnd = useCallback((event: ViewStateChangeEvent) => {
    setMapExtent(convertLngLatBoundsToExtent(event.target.getBounds()));
  }, []);

  const debouncedSearchString = useDebounce(searchString, 100);

  const allSchematisationsQuery = useAllHccSchematisationsListQuery();

  const allSchematisations = useMemo(
    () => allSchematisationsQuery.data?.results ?? [],
    [allSchematisationsQuery.data?.results]
  );

  const { data: tenantHasSchematisations, isLoading: tenantHasSchematisationsLoading } =
    useTenantHasSchematisationsQuery();

  const availableTags = useMemo(
    () =>
      Array.from(
        new Set(allSchematisations.flatMap((schematisation) => schematisation.tags ?? []))
      ).sort((a, b) => a.localeCompare(b)),
    [allSchematisations]
  );

  const filteredSchematisations = useMemo((): Schematisation[] => {
    // First pass: narrow the full catalogue to the current text and tag filters.
    if (!selectedTag && !debouncedSearchString) return allSchematisations;

    return allSchematisations.filter((schematisation) => {
      if (selectedTag && !(schematisation.tags ?? []).includes(selectedTag)) return false;

      if (!debouncedSearchString) return true;

      return schematisation.name.toLowerCase().includes(debouncedSearchString.toLowerCase());
    });
  }, [allSchematisations, selectedTag, debouncedSearchString]);

  const modelsQuery = useAllThreediModelsQuery({
    enabled: showingMap,
  });

  const schematisationItemsOnMap = useMemo(() => {
    // Only build map items for schematisations that survived the shared catalogue filters.
    return removeNullUndefined(
      (modelsQuery.data ?? []).map((model) => {
        if (
          !filteredSchematisations.some(
            (schematisation) => schematisation.id === model.schematisationId
          )
        ) {
          return undefined;
        }
        const bounds = combineHccModelExtents(model);
        const schematisationId = model.schematisationId;
        if (!bounds || typeof schematisationId !== "number") return undefined;

        const schematisationName =
          model.schematisationName || model.name || `Schematisation ${schematisationId}`;

        return {
          modelId: model.id as number,
          modelName: model.name || `Model ${model.id}`,
          schematisationId,
          schematisationName,
          bounds,
        } satisfies SchematisationMapItem;
      })
    );
  }, [filteredSchematisations, modelsQuery.data]);

  const schematisationExtents = useMemo(() => {
    const extents = new Map<number, Extent[]>();
    // Collect all extents per schematisation so map-view filtering can be done once here.
    for (const item of schematisationItemsOnMap) {
      const currentExtents = extents.get(item.schematisationId) ?? [];
      currentExtents.push(item.bounds);
      extents.set(item.schematisationId, currentExtents);
    }
    return extents;
  }, [schematisationItemsOnMap]);

  const mapFilteredSchematisations = useMemo(() => {
    // Second pass: apply the map viewport filter only when the user opted into it.
    if (!shouldFilterByMap || !mapExtent) return filteredSchematisations;
    return filteredSchematisations.filter((schematisation) => {
      if (typeof schematisation.id !== "number") return false;
      const extents = schematisationExtents.get(schematisation.id) ?? [];
      return extents.some((extent) => extentIntersects(extent, mapExtent));
    });
  }, [filteredSchematisations, shouldFilterByMap, mapExtent, schematisationExtents]);

  const isSchematisationsLoading =
    allSchematisationsQuery.isLoading || (showingMap && modelsQuery.isLoading);

  const schematisations = useMemo(
    () =>
      (isSchematisationsLoading ? [] : mapFilteredSchematisations).filter(
        (schematisation): schematisation is SchematisationWithId => !!schematisation.id
      ),
    [mapFilteredSchematisations, isSchematisationsLoading]
  );

  const getHccSchematisationQuery = useGetHccSchematisationQuery();
  const {
    data: schematisation,
    setDataId: setSelectedSchematisationId,
    setData: setSchematisation,
    isLoading: schematisationLoading,
  } = useQuerySearchState(SCHEMATISATION_SEARCH_KEY, getHccSchematisationQuery, {
    idIsNumber: true,
    disableSettingParams: disableSettingSearchParams,
    clearOthers: [REVISION_ID_KEY],
  });

  const [viewRevisionHistory, setViewRevisionHistory] = useRanaSearchState("history", false, {
    disableSettingParams: disableSettingSearchParams,
  });

  const [selectedRevisionId, setSelectedRevisionId] = useRanaSearchState<number | undefined>(
    REVISION_ID_KEY,
    undefined,
    ["number", "undefined"],
    {
      disableSettingParams: disableSettingSearchParams,
    }
  );

  const shouldShowRevisionHistory =
    schematisation && viewRevisionHistory && schematisation.id !== undefined;

  return {
    debouncedSearchString,
    selectedTag,
    setSelectedTag,
    availableTags,
    schematisations,
    isSchematisationsLoading,
    filterMap,
    setFilterMap,
    mapExtent,
    schematisation,
    setSelectedSchematisationId,
    setSchematisation,
    schematisationLoading,
    shouldShowRevisionHistory,
    setViewRevisionHistory,
    schematisationItemsOnMap,
    onMapMoveEnd,
    searchString,
    setSearchString,
    showingMap,
    setShowingMap,
    selectedRevisionId,
    setSelectedRevisionId,
    tenantHasSchematisations,
    tenantHasSchematisationsLoading,
  };
}
